import { ApolloError } from '@apollo/client';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAddJourneyConfig from './AddJourneyModal.config';
import { errorToast } from 'basics/utils/toast';
import { CreateReminderJourneyInput, EmailReminderType, ReminderChannel } from 'generated/event.types';
import useCreateReminderJourney from 'modules/events/graphql/mutations/createReminderJourney';
import Logger from 'services/logger';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useAddJourneyModal = (
  setOpen: Dispatch<SetStateAction<boolean>>,
  onJourneySave?: () => void,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createJourney } = useCreateReminderJourney();
  const { t } = useTranslation();
  const {
    getValues,
    providerFields,
    resetForm,
    useWatchForm,
  } = useYodaCreateForm();
  const { isValid } = useWatchForm();
  const { addJourneyConfig } = useAddJourneyConfig();

  const cancelForm = () => {
    resetForm();
    setOpen(false);
  };

  const handleSaveJourney = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const formValues = getValues();

      const input: CreateReminderJourneyInput = {
        name: formValues.name,
        sfmcJourneyApiKey: formValues.sfmcJourneyApiKey || '',
        format: formValues.format || EmailReminderType.before_start,
        timeInHour: formValues.timeInHour ? Number(formValues.timeInHour) : undefined,
        marketDefaults: formValues.marketDefaults || [],
        reminderType: formValues.reminderType || undefined,
        channel: formValues.channel || ReminderChannel.email,
        active: true,
      };

      await createJourney(input);

      if (onJourneySave) {
        onJourneySave();
      }

      resetForm();
      setOpen(false);
    } catch (err) {
      Logger.logError({
        error: err as Error,
        transactionName: 'AddJourneyModal - createJourney',
      });

      if (err instanceof ApolloError) {
        const errorMessage = err.graphQLErrors?.[0]?.message || err.message;
        errorToast(errorMessage);
      } else if (err instanceof Error) {
        errorToast(err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [createJourney, getValues, onJourneySave, resetForm, setOpen]);

  const cancelButtonConfig = {
    buttonProps: {
      buttonType: ButtonType.secondary,
      onClick: cancelForm,
    },
    label: t('actions_cancel_label'),
  };

  const submitButtonConfig = {
    buttonProps: {
      buttonType: ButtonType.primary,
      disabled: !isValid,
      onClick: handleSaveJourney,
    },
    label: t('actions_save_label'),
    loading: isSubmitting,
  };

  return {
    addJourneyConfig,
    cancelButtonConfig,
    providerFields,
    submitButtonConfig,
    t,
  };
};

export default useAddJourneyModal;
